
import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, ShieldCheck, Zap, Globe, MousePointer2 } from 'lucide-react';
import hr from "../../Assest/cards/consulting.avif";
import CTAButton from '../CTAButton';
import { GlobalEnquiryContext } from '../../App';

const HomeHeroSection = () => {
    const trustedBy = ["Startups", "GCCs", "Enterprises", "IT Companies", "Manufacturing", "High-Growth"];
    const { openEnquiry } = useContext(GlobalEnquiryContext);

    return (
        <section className="relative min-h-screen bg-white overflow-hidden flex flex-col">

            {/* --- BACKGROUND LAYER --- */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 opacity-[0.4]"
                    style={{
                        backgroundImage: `radial-gradient(circle, #e2e8f0 1.5px, transparent 1.5px)`,
                        backgroundSize: '40px 40px'
                    }} />

                <motion.div
                    animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
                    transition={{ duration: 8, repeat: Infinity }}
                    className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-blue-400 blur-[120px] rounded-full"
                />
                <motion.div
                    animate={{ scale: [1.2, 1, 1.2], opacity: [0.1, 0.15, 0.1] }}
                    transition={{ duration: 10, repeat: Infinity }}
                    className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-blue-200 blur-[120px] rounded-full"
                />
            </div>

            <div className="container mx-auto px-6 relative z-10 flex-grow flex items-center">
                <div className="flex flex-col lg:flex-row items-center justify-between gap-12 w-full pt-12">

                    {/* --- LEFT CONTENT --- */}
                    <div className="w-full lg:w-[50%] text-left space-y-8 z-30 pb-0 lg:pb-10">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-[10px] font-bold uppercase tracking-[0.3em]"
                        >
                            <Zap size={12} className="fill-current" />
                            Next-Gen Workforce Engineering
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-[#01014C] text-4xl md:text-6xl lg:text-4xl font-bold leading-[1.1] syne"
                        >
                            Build Smarter Workforce
                            <br /> Systems With
                            <span className="relative inline-block mx-4 my-2">
                                <span className="relative z-10 text-white px-6 py-2 italic font-bold text-[0.8em]">Ai-Powered</span>
                                <motion.div
                                    initial={{ scale: 0, rotate: -5 }}
                                    animate={{ scale: 1, rotate: -2 }}
                                    transition={{ type: 'spring', stiffness: 200, delay: 0.5 }}
                                    className="absolute inset-0 bg-blue-600 rounded-2xl shadow-[0_10px_30px_rgba(37,99,235,0.3)]"
                                />
                            </span>
                            HR Consulting Services
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="text-slate-500 text-lg max-w-xl leading-relaxed font-medium"
                        >
                            Helping businesses build stronger teams, faster hiring systems, <a href="service/hr-outsourcing/hr-compliance-services" className='underline text-blue-600 ' target="_blank">compliant HR operations </a>,
                            and scalable workforce strategies through highly reliable HR consulting services tailored
                            for modern organizations. 
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                            className="flex flex-wrap gap-5 mt-6"
                        >
                            <CTAButton 
                                text="Start Scaling"
                                onClick={() => openEnquiry("Start Scaling With Merida", [])}
                                type="primary"
                            />
                            <CTAButton 
                                text="Our Solutions"
                                onClick={() => {}}
                                type="outline-dark"
                            />
                        </motion.div>
                    </div>

                    {/* --- RIGHT VISUALS --- */}
                    <div className="w-full lg:w-[50%] relative flex justify-center items-end h-[500px] lg:h-auto lg:self-stretch mt-12 lg:mt-0">

                        <motion.div
                            key="top-float-card"
                            initial={{ rotate: 0 }}
                            animate={{ y: [0, -20, 0], rotate: 0 }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute top-10 right-[-20px] lg:right-[-80px] z-30 bg-white border border-slate-100 p-2 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] flex items-center gap-4"
                        >
                            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                                <Users size={20} />
                            </div>
                            <div>
                                <p className="text-[10px] text-blue-600 font-bold uppercase tracking-tighter">Candidate Match</p>
                                <p className="text-[#01014C] font-black">98.4% Accuracy</p>
                            </div>
                        </motion.div>

                        <motion.div
                            animate={{ y: [0, 20, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            className="absolute bottom-20 left-0 z-30 bg-white border border-slate-100 p-2 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] flex items-center gap-4"
                        >
                            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white">
                                <ShieldCheck size={20} />
                            </div>
                            <div>
                                <p className="text-[10px] text-blue-500 font-bold uppercase tracking-tighter">Compliance</p>
                                <p className="text-[#01014C] font-black">100% Audit Ready</p>
                            </div>
                        </motion.div>

                        <motion.div
                            key="hero-image"
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1 }}
                            className="absolute bottom-0 z-20 w-full flex justify-center"
                        >
                            <img
                                src={hr}
                                alt="HR Consulting Expert"
                                className="w-[160%] max-w-none h-auto max-h-[750px] lg:max-h-[1100px] object-contain object-bottom relative z-10 drop-shadow-[0_20px_40px_rgba(0,0,0,0.15)] -mr-[30%]"
                            />
                        </motion.div>

                        <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ duration: 3, repeat: Infinity }} className="absolute top-1/4 left-10 w-2 h-2 bg-blue-400 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.3)]" />
                        <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ duration: 4, repeat: Infinity, delay: 0.5 }} className="absolute bottom-1/3 right-10 w-3 h-3 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.3)]" />
                    </div>
                </div>
            </div>

            {/* --- BOTTOM MARQUEE (Fixed with Framer Motion) --- */}
            <div className="relative w-full border-t border-slate-100 bg-slate-50/50 backdrop-blur-xl py-6 overflow-hidden">
                <motion.div 
                    className="flex items-center gap-12 w-max"
                    animate={{ x: ["0%", "-50%"] }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                >
                    <span className="text-[#01014C]/30 font-black uppercase tracking-[0.4em] text-[10px] pl-12">
                        Trusted Workforce Partner
                    </span>
                    {/* Rendered twice for seamless loop */}
                    {[...trustedBy, ...trustedBy, ...trustedBy].map((item, index) => (
                        <div key={index} className="flex items-center gap-6">
                            <Globe size={14} className="text-blue-500" />
                            <span className="text-slate-700 font-bold text-sm tracking-wide uppercase">{item}</span>
                            <div className="w-1 h-1 bg-slate-300 rounded-full" />
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default HomeHeroSection;